import { useState, useEffect } from 'react';
import LandingPage from '../components/LandingPage';
import { detectLocale } from '../locales/config';

const SPANISH_LOCALES = ['es-mx', 'es-es', 'es-ar', 'es-co', 'es-cl'];

const faqItems = [
  {
    q: '¿Cómo funciona la calculadora de deudas?',
    a: 'Ingresa cada deuda con su saldo, tasa de interés anual y pago mínimo mensual. La calculadora aplica el método bola de nieve o avalancha para mostrarte tu fecha exacta de liberación de deudas, el total de intereses que pagarás y cuánto ahorras pagando un poco extra cada mes.',
  },
  {
    q: '¿Cuál es mejor: método bola de nieve o avalancha?',
    a: 'Depende de tu perfil. El método avalancha (pagar primero la deuda con mayor tasa) te ahorra más dinero en intereses. El método bola de nieve (pagar primero la deuda más pequeña) te da victorias rápidas que mantienen la motivación. Ambos son mucho más eficaces que pagar solo el mínimo.',
  },
  {
    q: '¿Cuánto tiempo tarda en pagar mis deudas?',
    a: 'Depende de tu saldo, tasa de interés y cuánto pagas cada mes. Con una deuda de $50,000 pagando solo el mínimo puedes tardar más de 10 años. Destinando dinero extra al pago puedes lograrlo en 2–3 años y ahorrar miles en intereses. Usa el simulador de deudas para ver tu plan personalizado.',
  },
  {
    q: '¿Es gratis esta calculadora?',
    a: 'La calculadora de deudas es completamente gratuita y no requiere registro. Puedes calcular tu plan para pagar deudas en segundos. Si quieres guardar y descargar tu plan personalizado en PDF, existe una opción de pago único sin suscripción.',
  },
  {
    q: '¿Funciona para tarjetas de crédito y préstamos?',
    a: 'Sí. Esta calculadora de pago de deuda funciona para tarjetas de crédito, préstamos personales, créditos automotrices, préstamos estudiantiles y cualquier tipo de deuda con interés. Puedes ingresar varias deudas a la vez para calcular el tiempo total para salir de deudas.',
  },
];

const COLORS = { text: "#e8e8f0", muted: "#9999b5", accent: "#00e5a0" };

const sections = [
  {
    heading: 'Cómo usar este simulador de deudas',
    body: 'Ingresa cada deuda: saldo actual, tasa de interés anual y pago mínimo mensual. Añade cualquier monto extra que puedas destinar al pago. La calculadora de pago de deuda muestra tu fecha de liberación, el total de intereses y cómo cada estrategia — bola de nieve o avalancha — acelera tu salida de deudas.',
  },
  {
    heading: 'Método bola de nieve vs avalancha: ¿cuál elegir?',
    body: (
      <>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 12 }}>
          El <strong style={{ color: COLORS.text }}>método bola de nieve de deudas</strong> consiste en
          pagar primero la deuda más pequeña y usar ese pago liberado para atacar la siguiente.
          Es ideal si necesitas motivación y victorias rápidas en tu plan para pagar deudas.
        </p>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 0 }}>
          El <strong style={{ color: COLORS.text }}>método avalancha</strong> ataca primero la deuda con
          mayor tasa de interés. Matemáticamente es más eficiente y reduce más el costo total.
          Usa nuestro simulador de deudas para calcular cuánto ahorras con cada método.
        </p>
      </>
    ),
  },
  {
    heading: 'Por qué los pagos mínimos te cuestan años de libertad financiera',
    body: 'El interés de las tarjetas de crédito se acumula mensualmente sobre el saldo pendiente. Pagando solo el mínimo, una deuda mediana puede tardarte más de 8–10 años en saldar. Agregar aunque sea un 10–15% extra al pago mínimo puede reducir ese tiempo a la mitad y ahorrarte miles en intereses. Usa la calculadora de deudas para calcular tiempo pagar deuda y ver el impacto exacto de cada peso extra que destines.',
  },
];

export default function CalculadoraDeDeudas() {
  const [locale, setLocale] = useState('es-mx');

  useEffect(() => {
    detectLocale().then(detected => {
      setLocale(SPANISH_LOCALES.includes(detected) ? detected : 'es-mx');
    });
  }, []);

  return (
    <LandingPage
      locale={locale}
      pageUrl="https://clearmydebt.app/calculadora-de-deudas/"
      helmet={{
        lang: 'es',
        title: 'Calculadora de Deudas Gratis — Salir de Deudas Rápido | ClearMyDebt',
        description: 'Calcula cuándo estarás libre de deudas. Método bola de nieve y avalancha. Sin registro. Resultados instantáneos.',
        ogTitle: 'Calculadora de Deudas Gratis — Salir de Deudas Rápido | ClearMyDebt',
        ogDescription: 'Calcula cuándo estarás libre de deudas. Método bola de nieve y avalancha. Sin registro. Resultados instantáneos.',
      }}
      hreflangList={[
        { hreflang: 'es-mx', href: 'https://clearmydebt.app/calculadora-de-deudas/' },
        { hreflang: 'es-es', href: 'https://clearmydebt.app/calculadora-de-deudas/' },
        { hreflang: 'es-ar', href: 'https://clearmydebt.app/calculadora-de-deudas/' },
        { hreflang: 'es-co', href: 'https://clearmydebt.app/calculadora-de-deudas/' },
        { hreflang: 'es-cl', href: 'https://clearmydebt.app/calculadora-de-deudas/' },
        { hreflang: 'x-default', href: 'https://clearmydebt.app/calculadora-de-deudas/' },
      ]}
      h1="Calculadora de Deudas Gratis"
      intro="Ingresa tus deudas y descubre exactamente cuándo estarás libre de deudas — y cuánto dinero ahorrarás con el plan correcto para pagar deudas."
      sections={sections}
      faqItems={faqItems}
      faqHeading="Preguntas Frecuentes"
      moreHeading="Más calculadoras"
      moreLinks={[
        { href: '/es-mx/credit-card-payoff-calculator/', label: 'Calculadora tarjeta de crédito' },
        { href: '/es-mx/debt-snowball-calculator/', label: 'Método bola de nieve' },
        { href: '/es-mx/debt-avalanche-calculator/', label: 'Método avalancha' },
        { href: '/es-mx/debt-repayment-calculator/', label: 'Calculadora de pagos' },
      ]}
    />
  );
}
